import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Halil Aksu's contact page.
 *
 * Asked for 2026-09-15 at `/en/about/team/halil-aksu`, which is where the
 * request named it, so that address is the slug. The page is a contact card
 * and nothing more: the name, the title, and the three channels he gave —
 * email and one number per country. No biography is invented here; what the
 * site says about the three co-CEOs stays on About Us, which this page links
 * back to.
 *
 * The details are the `features` grid rather than prose because each one is a
 * thing to act on: the value is the link (`mailto:`/`tel:`) and the label sits
 * under it, so a phone dials from the page instead of asking the reader to
 * copy a number out of a sentence. `localizeHref` passes both schemes through
 * untouched.
 *
 * Asked for 2026-09-15: the team contact pages should read as one set, so this
 * page's shape is the shared one — role in the eyebrow, one line of role under
 * a centred name, a `features` grid whose card title *is* the address or the
 * number, and the same closing band back to the firm.
 */
export const route: RouteDef = {
  id: 'teamHalilAksu',
  path: 'about/team/halil-aksu',
  // Asked for 2026-09-15: these contact cards are handed out as QR codes, so
  // nothing on the site should lead to them — out of the sitemap and out of
  // search results, with the About Us card back on LinkedIn. The URL still
  // works for anyone who scans the code.
  robots: 'noindex',
  priority: 0.5,
}

const EMAIL = 'halil@digitopia.ai'
const LINKEDIN = 'https://www.linkedin.com/in/halilaksu/'
// Asked for 2026-09-17: a button beside LinkedIn that saves the details
// straight into the reader's phone. The file is a static vCard in
// `public/vcf/`, named after this page's slug — see that folder's README. A
// plain link rather than JavaScript, because the card is reached by QR code
// from a phone and an attribute works whether or not the bundle has loaded.
const VCARD = '/vcf/halil-aksu.vcf'
// Asked for 2026-09-28: a "Book a call" link on this page only — the one
// reached by scanning the QR code — pointing at Halil's own Calendly.
const CALENDLY = 'https://calendly.com/halil-digitopia/30min'

export const teamHalilAksu: WrittenPage = {
  navLabel: 'Halil Aksu',
  title: 'Halil Aksu: Co-CEO, Growth',
  description:
    'Contact Halil Aksu, Co-CEO of Digitopia for Growth: email halil@digitopia.ai, or call +1 302 444 2632 (US) or +90 532 722 74 87 (Türkiye).',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Co-CEO, Growth',
      headline: 'Halil Aksu',
      align: 'center',
      // No portrait, asked for 2026-09-15: this page is scanned from a QR code
      // on a card that already has his face on it, so it is a set of details
      // rather than a profile.
      ctas: [
        { label: 'Book a call', href: CALENDLY },
        { label: 'Email', href: `mailto:${EMAIL}` },
        { label: 'LinkedIn', href: LINKEDIN },
        { label: 'Save contact', href: VCARD },
      ],
    },
    {
      kind: 'features',
      heading: 'Get in touch',
      columns: 3,
      items: [
        {
          title: EMAIL,
          note: 'Email',
          href: `mailto:${EMAIL}`,
        },
        {
          title: '+1 302 444 2632',
          note: 'United States',
          href: 'tel:+13024442632',
        },
        {
          title: '+90 532 722 74 87',
          note: 'Türkiye',
          href: 'tel:+905327227487',
        },
      ],
      footnote: 'Digitopia works from New York, London and Istanbul.',
    },
    {
      kind: 'cta',
      heading: 'Rather start with the firm than a person?',
      sub: 'The contact form reaches the whole team, and About Us says who else you would be working with.',
      tone: 'band',
      ctas: [
        { label: 'Contact Digitopia', href: LINKS.contact },
        { label: 'About Digitopia', href: LINKS.about },
      ],
    },
  ],
}
